import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { NotificationService } from '@rentigo/ngx-common';
import { AuthorizationConfig, UserService } from '@rentigo/ngx-authorization';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { ManagerSettingsService } from '../services/manager-settings.service';
import { canOpenSdDestination, firstSdDestination } from '../models/sd-navigation';

@Injectable()
export class AuthorizePolicyGuard implements CanActivate {
  constructor(
    private router: Router,
    private notificationService: NotificationService,
    private config: AuthorizationConfig,
    private userService: UserService,
    private managerSettingsService: ManagerSettingsService) {
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
    if (!this.userService.getUser().isAuthenticated) {
      this.managerSettingsService.clear();
      return this.router.parseUrl(this.config.loginUrl ?? '/login');
    }

    if (next.data['sdDestination'] || next.data['sdLanding']) {
      return this.managerSettingsService.getSettings().pipe(
        switchMap(settings => {
          const requested = next.data['sdDestination'];
          if (requested !== 'rankings') {
            if (requested && canOpenSdDestination(settings.rights, requested, false)) return of(true);
            const first = firstSdDestination(settings.rights, false);
            if (first) return of(this.router.createUrlTree(['/security-deposit', first]));
          }
          return this.managerSettingsService.getAccess().pipe(map(({ settings, rankingsEnabled }) => {
            if (requested && canOpenSdDestination(settings.rights, requested, rankingsEnabled)) return true;
            const destination = firstSdDestination(settings.rights, rankingsEnabled);
            if (destination) return this.router.createUrlTree(['/security-deposit', destination]);
            this.notificationService.error('No Security Deposit sections are available. Contact your administrator.');
            return this.router.parseUrl('/');
          }));
        }),
        catchError(() => {
          this.notificationService.error('Unable to load Security Deposit permissions. Please try again.');
          return of(this.router.parseUrl('/'));
        })
      );
    }

    if (this.userService.isPolicySatisfied(next.data['policy'])) return true;
    if (next.data['next']) return this.router.createUrlTree(['security-deposit', next.data['next']]);
    this.notificationService.error('Access denied');
    return this.router.parseUrl(this.config.loginUrl ?? '/login');
  }
}
